import type { Dispatch, RefObject } from "react";
import type { AudioPlaybackAction } from "../types/audio-playback";

import { useEffect } from "react";
import { getAudioDurationSeconds } from "../utils/get-audio-duration-seconds";

type UseAudioElementEventHandlers = {
  onEnded?: (audio: HTMLAudioElement) => void;
  onError?: (audio: HTMLAudioElement) => void;
  onLoadedMetadata?: (audio: HTMLAudioElement) => void;
  onPause?: (audio: HTMLAudioElement) => void;
  onPlay?: (audio: HTMLAudioElement) => void;
  onWaiting?: (audio: HTMLAudioElement) => void;
};

type UseAudioElementEventsOptions = {
  audioElement: HTMLAudioElement | null;
  audioSource: string;
  dispatch: Dispatch<AudioPlaybackAction>;
  eventHandlersReference: RefObject<UseAudioElementEventHandlers | undefined>;
};

function useAudioElementEvents({
  audioElement,
  audioSource,
  dispatch,
  eventHandlersReference,
}: UseAudioElementEventsOptions) {
  useEffect(() => {
    const audio = audioElement;

    if (!audio) {
      return;
    }

    const handleLoadedMetadata = () => {
      const duration = getAudioDurationSeconds(audio);

      dispatch({
        currentTimeSeconds: audio.currentTime,
        durationSeconds: duration,
        type: "metadata-loaded",
      });
      eventHandlersReference.current?.onLoadedMetadata?.(audio);
    };
    const handleTimeUpdate = () => {
      dispatch({
        currentTimeSeconds: audio.currentTime,
        type: "time-updated",
      });
    };
    const handlePlay = () => {
      dispatch({ type: "play" });
      eventHandlersReference.current?.onPlay?.(audio);
    };
    const handlePause = () => {
      dispatch({ type: "pause" });
      eventHandlersReference.current?.onPause?.(audio);
    };
    const handleEnded = () => {
      dispatch({
        currentTimeSeconds: getAudioDurationSeconds(audio) ?? audio.currentTime,
        type: "ended",
      });
      eventHandlersReference.current?.onEnded?.(audio);
    };
    const handleError = () => {
      dispatch({ type: "error" });
      eventHandlersReference.current?.onError?.(audio);
    };
    const handleWaiting = () => {
      dispatch({
        isPlaying: !audio.paused,
        type: "waiting",
      });
      eventHandlersReference.current?.onWaiting?.(audio);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);
    audio.addEventListener("waiting", handleWaiting);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("waiting", handleWaiting);
      audio.pause();
      audio.currentTime = 0;
    };
  }, [audioElement, audioSource, dispatch, eventHandlersReference]);
}

export { useAudioElementEvents };
